<template>
  <div v-if="loaded">

    <!-- ── Full-viewport paywall (position:fixed, always on top) ── -->
    <div v-if="previewExceeded && !is_user_can_watch" id="preview_paywall" role="dialog" aria-modal="true" aria-label="נדרש מנוי">
      <div
        class="vp-paywall-bg"
        :style="item.pic_hero_parsed ? 'background-image:url(' + item.pic_hero_parsed + ')' : ''"
        aria-hidden="true"
      ></div>
      <div class="vp-paywall-overlay" aria-hidden="true"></div>
      <div class="vp-paywall-content" role="alert" aria-live="polite">
        <div class="vp-paywall-badge" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span>TOV+</span>
        </div>
        <h2 class="vp-paywall-headline">היי {{ user_data.display_name }},</h2>
        <p class="vp-paywall-sub">צפיתם בתצוגה המקדימה (2 דקות).<br>סיימו את ההרשמה כדי להמשיך לצפות.</p>
        <button type="button" class="vp-cta-btn" @click="get_me ? $router.push('/user?section=billing') : $store.commit('open_signup', { step: 1, plan_id: 0 })">סיימו את ההרשמה עכשיו</button>
      </div>
      <button type="button" class="vp-paywall-close" aria-label="סגור" @click="$router.go(-1)">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>

    <!-- ── Country-blocked screen (position:fixed) ── -->
    <div id="unactive_screen" v-if="!is_country_can_watch">
      <div class="vp-bg" :style="'background-image:url('+ item.pic_hero_parsed +')'"></div>
      <button type="button" class="vp-nav-btn tov_animate" aria-label="עבור לדף הבית" @click="$router.replace('/')">
        <svg class="vp-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <div class="vp-inner">
        <div class="restriction-message" role="alert" aria-live="polite">
          <div><b>היי {{user_data.display_name}}.</b></div>
          לצערנו השירות לא פועל במדינה שבה אתה נמצא.<br>
        </div>
      </div>
    </div>

    <!-- ── Profile-restricted screen (position:fixed) ── -->
    <div id="unactive_screen" v-if="item.restricted == true">
      <div class="vp-bg" :style="'background-image:url('+ item.pic_hero_parsed +')'"></div>
      <button type="button" class="vp-nav-btn tov_animate" aria-label="עבור לדף הבית" @click="$router.replace('/')">
        <svg class="vp-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <div class="vp-inner">
        <div class="restriction-message" role="alert" aria-live="polite">
          <div><b>היי {{user_data.display_name}}.</b></div>
          הסרט חסום לפי הגדרות הפרופיל שלך.<br>
        </div>
      </div>
    </div>

    <!-- ── Main watch-page layout ── -->
    <div
      id="video_page"
      :class="is_user_active ? 'user_active' : 'user_inactive'"
      @mousemove="user_hover()"
      @touchmove="user_hover()"
      @click.capture="on_page_click"
    >

      <!-- ① PLAYER ROW (player + optional chat sidebar) -->
      <div class="vp-player-row">

        <!-- Video wrapper: sized box, NOT full viewport -->
        <div
          id="video_wrapper"
          :class="episode.is_chat == 1 ? 'small_video_wrapper' : ''"
          @mouseover="show_controls = true"
          @mouseleave="show_controls = false"
        >
          <!-- Preview blocker -->
          <div v-if="previewExceeded && !is_user_can_watch" class="vp-preview-blocker" aria-hidden="true"></div>

          <div class="vp-controls-top">
            <button type="button" id="go_back_btn" class="tov_animate" aria-label="חזור אחורה" @click="$router.go(-1)">
              <svg class="vp-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <button type="button" id="go_home_btn" class="tov_animate" aria-label="עבור לדף הבית" @click="$router.replace('/')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </button>
          </div>

          <!-- Movie title overlay (fades after 5 s) 
          <div id="movie_titles" :class="{ 'movie_titles--hidden': playingPast5s }">
            <h3 v-text="item.title"></h3>
            <h4 v-if="item.title != episode.title" v-text="episode.title"></h4>
          </div>-->

          <!-- The player itself -->
          <UiVideoPlayer
            ref="videoPlayer"
            :src="videoSrc"
            :poster="episode.pic_thumb_parsed"
            :autoplay="true"
            :controls="true"
            :start-time="episode.user_duration > 0 ? episode.user_duration : 0"
            :srt-list="episode.srt_list || []"
            :extra-options="{ playbackRates: [0.5, 1, 1.5, 2] }"
            @timeupdate="onTimeupdate"
            @pause="save_user_watch_time"
            @ended="save_user_watch_time"
            @playerCreated="onPlayerCreated"
          />

          <!-- Restriction bubbles / skip actions -->
          <div id="rest_bubbles" v-if="is_user_can_watch">
            <button
              type="button"
              class="vp-skip-intro"
              v-if="player && is_playing && currentTime >= opening_starts && currentTime < opening_ends"
              @click="$refs.videoPlayer.seek(opening_ends)"
            >דלג על הפתיחה</button>

            <button
              type="button"
              class="vp-next-episode"
              v-if="player && is_playing && next_episode.id > 0 && currentTime >= ending_starts"
              @click="$router.push('/item/' + next_episode.item_id + '/' + next_episode.id)"
            >
              <div
                class="vp-next-episode__thumb"
                v-if="next_episode.pic_thumb_parsed"
                :style="'background-image:url(' + next_episode.pic_thumb_parsed + ')'"
                aria-hidden="true"
              ></div>
              <div class="vp-next-episode__body">
                <span class="vp-next-episode__label">הפרק הבא</span>
                <span class="vp-next-episode__title" v-if="next_episode.title">{{ next_episode.title }}</span>
              </div>
              <svg class="vp-next-episode__arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
            </button>

            <div v-for="rest_item in rest_comp" :key="rest_item.id" class="restriction_item">
              <h3>{{ rest_item.cat.name }}</h3>
              <div class="content">{{ rest_item.description }}</div>
              <button type="button" class="vp-skip-btn" @click="$refs.videoPlayer.seek(rest_item.time_end_seconds)">דלג</button>
            </div>
          </div>
        </div><!-- /#video_wrapper -->

        <!-- Chat sidebar (only when is_chat == 1) -->
        <div id="chat" v-if="episode.is_chat == 1" :key="is_chat_open" :class="is_chat_open ? '' : 'chat_close'">
          <button
            v-if="is_mobile() && !is_chat_open"
            type="button"
            class="open_chat"
            aria-label="פתח צ'אט"
            @click="is_chat_open = true"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            v-if="is_mobile() && is_chat_open"
            type="button"
            class="open_chat"
            aria-label="סגור צ'אט"
            @click="is_chat_open = false"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <Chat :episode="episode" @open_pay="$emit('open_pay')" :active="is_user_can_watch"/>
        </div>

      </div><!-- /.vp-player-row -->

      <!-- ② BELOW-PLAYER CONTENT -->
      <div class="vp-content">

        <!-- Hero block: title + meta + actions -->
        <header class="vp-hero">
          <div class="vp-hero__head">
            <div class="vp-hero__head-right">
            <span class="vp-meta__pill vp-pill--accent">{{ typeLabel }}</span>
            <span v-if="item.year" class="vp-meta__chip">{{ item.year }}</span>
            <span v-if="episode.duration_formatted" class="vp-meta__chip">{{ episode.duration_formatted }}</span>
            <span v-if="item.main_category && item.main_category.name" class="vp-meta__chip">{{ item.main_category.name }}</span>
            </div>
            <div class="vp-hero__head-left">
                <button
              v-if="item.restricted != true"
              type="button"
              class="vi-action-btn"
              :class="{ 'vi-action-btn--active': isFavorite }"
                  @click="add_to_fav(item.id)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" :fill="isFavorite ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              {{ isFavorite ? 'הסר ממועדפים' : 'הוספה למועדפים' }}
            </button>
            </div>
          </div>

          <h1 class="vp-title">{{ item.title }}</h1>
          <h2 class="vp-episode-title" v-if="episode.title && episode.title !== item.title">{{ episode.title }}</h2>

          <!-- Primary actions 
          <div class="vp-action-row">
            <button
              type="button"
              class="vp-action-btn vp-action-btn--primary"
              @click="$refs.videoPlayer.requestFullscreen()"
              aria-label="צפה במסך מלא"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
              <span>צפייה במסך מלא</span>
            </button>
            <button
              type="button"
              class="vp-action-btn"
              @click="$router.go(-1)"
              aria-label="חזור אחורה"
            >
              <svg class="vp-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
              <span>חזרה</span>
            </button>
          </div>
-->
          <!-- Description -->
          <p class="vp-description" v-if="item.description">{{ item.description }}</p>
        </header>

        <!-- ③ EPISODES (series only, inline) -->
        <section class="vp-episodes" v-if="item.type === 'series' && seasons && seasons.length">
          <div class="vp-episodes__header">
            <h3 class="vp-section-title">פרקים</h3>
            <nav class="vp-seasons-nav" aria-label="ניווט עונות">
              <button
                v-for="season in seasons"
                :key="season.id"
                type="button"
                class="vp-season-tab"
                :class="{ 'vp-season-tab--active': activeSeason === season.id }"
                @click="activeSeason = season.id"
              >עונה {{ season.num }}</button>
            </nav>
          </div>

          <ul
            v-for="season in seasons"
            :key="season.id"
            v-show="activeSeason === season.id"
            class="vp-ep-list"
            role="list"
          >
            <li v-for="episode_item in season.episodes" :key="episode_item.id">
              <UiCard
                variant="episode"
                :to="'/item/' + item.id + '/' + episode_item.id + '?autoplay=true'"
                :class="{ 'ui-card--episode-active': episode_item.id == episode.id }"
              >
                <template #thumbnail>
                  <div
                    class="ep-thumb"
                    :style="'background-image:url(' + episode_item.pic_thumb_parsed + ')'"
                    role="img"
                    :aria-label="episode_item.title"
                  >
                    <div class="ep-thumb__progress">
                      <span :style="'width:' + episode_item.user_duration_perc + '%'"></span>
                    </div>
                  </div>
                </template>
                <div class="ep-content">
                  <h3 class="ep-title">{{ episode_item.title }}</h3>
                  <p class="ep-desc">{{ episode_item.description }}</p>
                </div>
              </UiCard>
            </li>
          </ul>
        </section>

        <!-- ④ RELATED ITEMS RAIL -->
        <section class="vp-related" v-if="relatedRailItems.length">
          <div class="rail__header">
            <h3 class="rail__title">{{ relatedRailTitle }}</h3>
          </div>
          <section ref="relSplide" class="splide rail__splide">
            <div class="splide__track">
              <ul class="splide__list">
                <li v-for="rel in relatedRailItems" :key="rel.id" class="splide__slide">
                  <item-tpl :item="rel" :lazy="false" />
                </li>
              </ul>
            </div>
          </section>
        </section>

      </div><!-- /.vp-content -->

    </div><!-- /#video_page -->

    <md-dialog-prompt
      :md-active.sync="active_problam"
      v-model="problam_val"
      md-title="בואו תספרו לנו מה הבעיה?"
      md-input-maxlength="150"
      md-input-placeholder="הקלד/י את הבעיה..."
      @md-confirm="onConfirm_problam()"
      md-confirm-text="שלח"
      md-cancel-text="בטל" />

  </div>
</template>

<script>
import Chat from '../components/chat.vue';
import UiVideoPlayer from './ui/VideoPlayer.vue';
import UiCard from './ui/Card.vue';
import itemTpl from './item_tpl.vue';
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide.min.css';

const REL_SPLIDE_OPTS = {
  type: 'loop',
  direction: 'rtl',
  perPage: 5,
  perMove: 1,
  gap: '18px',
  pagination: false,
  arrows: true,
  drag: true,
  speed: 600,
  breakpoints: {
    1280: { perPage: 4, perMove: 1 },
    1024: { perPage: 3, perMove: 1 },
    768:  { perPage: 3, perMove: 1 },
    640:  { perPage: 2, perMove: 1 },
  },
};

export default {
  components: { Chat, UiVideoPlayer, UiCard, itemTpl },
  props: ['user_data', 'profile', 'country_code'],

  data() {
    return {
      updateInterval: false,
      user_token: localStorage.getItem('user_token'),
      auto_skip_rest: [],
      user_watch_time: 0,
      previewSecondsWatched: 0,
      previewExceeded: false,
      PREVIEW_LIMIT_SECONDS: 120,
      status: '',
      link: '/info/',
      coupon: '',
      problam_val: '',
      active_problam: false,
      coupon_active: false,
      show: true,
      show_dets: true,
      is_chat_open: true,
      is_user_active: false,
      is_user_active_timer: null,
      opening_starts: 0,
      opening_ends: 0,
      ending_starts: 0,
      show_controls: false,
      player: null,
      loaded: false,
      id: this.$route.params.id,
      eid: this.$route.params.eid,
      episode: {},
      seasons: [],
      activeSeason: null,
      item: {},
      related_items: [],
      related_mode: 'same_category',
      isFavorite: false,
      cur_season: {},
      next_episode: {},
      relSplide: null,
      utm_source: localStorage.getItem('utm_source') || '',
      utm_medium: localStorage.getItem('utm_medium') || '',
      utm_campaign: localStorage.getItem('utm_campaign') || '',
    };
  },

  computed: {
    is_user_can_watch() {
      if (!this.get_me) return false;
      return this.get_me.is_active == true;
    },
    is_country_can_watch() {
      return true;
    },
    videoSrc() {
      if (!this.episode || !this.episode.video) return '';
      return this.episode.video;
    },
    is_playing() {
      if (this.isNull(this.player)) return false;
      return this.player.currentTime() > 0 && !this.player.paused() && !this.player.ended();
    },
    currentTime: {
      get() { return this.player ? this.player.currentTime() : 0; },
      set(v) { if (this.player) this.player.currentTime(v); },
    },
    playingPast5s() {
      return this.is_playing && this.currentTime > 5;
    },
    typeLabel() {
      if (this.item.type === 'live') return 'שידור חי';
      if (this.item.type === 'series') return 'סדרה';
      return 'סרט';
    },
    relatedRailItems() {
      return Array.isArray(this.related_items) ? this.related_items : [];
    },
    relatedRailTitle() {
      return this.related_mode === 'same_category' ? 'עוד באותה קטגוריה' : 'מוצגים אחרים';
    },
    rest_comp() {
      var arr = [];
      var pre_show_alert = 0;
      if (this.profile.perms_auto_skip == '0' && this.profile.perms_show_alerts == '1') pre_show_alert = 4;

      for (let z in this.episode.restrictions) {
        for (let i in this.episode.restrictions[z].items) {
          let rest_item = this.episode.restrictions[z].items[i];
          if (this.player && this.currentTime >= (rest_item.time_start_seconds - pre_show_alert) && this.currentTime <= rest_item.time_end_seconds) {
            if (this.profile.perms_auto_skip == '1') {
              this.$refs.videoPlayer.seek(rest_item.time_end_seconds + 1);
              return;
            } else if (this.profile.perms_show_alerts == '1') {
              rest_item.cat = this.episode.restrictions[z].data.name;
              arr.push(rest_item);
            }
          }
        }
      }
      return arr;
    },
  },

  beforeRouteLeave(to, from, next) {
    clearInterval(this.updateInterval);
    this.save_user_watch_time();
    next();
  },

  methods: {
    onConfirm_problam() {
      this.api_call({
        url: 'problem',
        data: { uid: this.get_me.id, eid: this.eid, message: this.problam_val },
        onComplete: () => { this.$alert('הודעתך נשלחה בהצלחה!'); },
      });
    },

    add_to_fav(id) {
      if (!this.get_me || !this.get_me.id) {
        this.$store.commit('open_signup', { step: 1, plan_id: 0 });
        return;
      }

      const userToken = localStorage.getItem('user_token');
      if (!userToken) {
        this.$store.commit('open_signup', { step: 1, plan_id: 0 });
        return;
      }

      if (this.isFavorite) {
        this.$http.post(window.api_url + 'user/remove_favorite', {
          item_id: id,
          api_user_token: userToken,
        }).then(() => {
          this.isFavorite = false;
          this.$alert('הסרט הוסר מרשימת הצפייה שלך');
        }).catch(() => {
          this.$alert('לא הצלחנו להסיר מהמועדפים, נסו שוב.');
        });
      } else {
        this.$http.post(window.api_url + 'user/add_favorite', {
          item_id: id,
          api_user_token: userToken,
        }).then(() => {
          this.isFavorite = true;
          this.$alert('הסרט נוסף לרשימת הצפייה שלך');
        }).catch(() => {
          this.$alert('לא הצלחנו להוסיף למועדפים, נסו שוב.');
        });
      }
    },

    user_hover() {
      this.user_hover_with_delay(2400);
    },

    user_hover_with_delay(delayMs) {
      this.is_user_active = true;
      window.clearTimeout(this.is_user_active_timer);
      this.is_user_active_timer = setTimeout(() => { this.is_user_active = false; }, delayMs);
    },

    on_page_click(event) {
      const target = event && event.target;
      if (!target || typeof target.closest !== 'function') return;

      const clickedInsideVideoArea = !!target.closest('#video_wrapper .ui-video-player, #video_wrapper .video-js, #video_wrapper video, #video_wrapper iframe');

      if (clickedInsideVideoArea) {
        this.user_hover_with_delay(2400);
        return;
      }

      window.clearTimeout(this.is_user_active_timer);
      this.is_user_active = false;
    },

    save_user_watch_time() {
      var settings = {
        profile_id: this.profile.id,
        episode_id: this.item.episode.id,
        not_free_user: (this.get_me.is_active ? 1 : 0),
        item_id: this.item.episode.item_id,
        season_id: this.item.episode.season_id,
        duration: this.user_watch_time,
      };
      this.$http.post(window.api_url + 'user/update_time', settings).then(function() {});
    },

    onPlayerCreated(vjsPlayer) {
      var self = this;
      self.player = vjsPlayer;

      var saved_skip = JSON.parse(localStorage.getItem('user_restrictions'));
      var rest_flat = [];
      for (let z in self.episode.restrictions) {
        for (let i in self.episode.restrictions[z].items) {
          rest_flat.push(self.episode.restrictions[z].items[i]);
        }
      }
      for (let i in rest_flat) {
        if (!self.isNull(saved_skip) && saved_skip[rest_flat[i].id] === true) {
          self.auto_skip_rest.push(rest_flat[i]);
        }
      }

      self.updateInterval = setInterval(function() {
        if (self.is_playing) self.save_user_watch_time();
      }, 15000);

      var videojs = vjsPlayer.constructor;

      var info_btn = videojs.extend(videojs.getComponent('Button'), {
        handleClick: function() { self.$router.push('/info/' + self.item.id); }
      });
      var problam_btn = videojs.extend(videojs.getComponent('Button'), {
        handleClick: function() { self.active_problam = true; }
      });
      var next_episode_btn = videojs.extend(videojs.getComponent('Button'), {
        handleClick: function() {
          self.$router.push('/item/' + self.next_episode.item_id + '/' + self.next_episode.id);
        }
      });
      var episode_name = videojs.extend(videojs.getComponent('Button'));

      var isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      vjsPlayer.controlBar.left_group.addChild(new info_btn()).addClass('info-btn');
      if (!isMobile) vjsPlayer.controlBar.left_group.addChild(new problam_btn()).addClass('problam_btn');

      if (self.next_episode && self.next_episode.id > 0) {
        vjsPlayer.controlBar.right_group.addChild(new next_episode_btn()).addClass('next-episode-btn');
      }
      vjsPlayer.controlBar.right_group.addChild(new episode_name());
    },

    onTimeupdate({ currentTime }) {
      this.user_watch_time = currentTime;

      if (!this.is_user_can_watch && !this.previewExceeded) {
        if (currentTime >= this.PREVIEW_LIMIT_SECONDS) {
          this.previewExceeded = true;
          localStorage.setItem('preview_used_' + this.item.id, '1');
          if (this.$refs.videoPlayer) this.$refs.videoPlayer.pause();
          return;
        }
      }

      if (this.is_user_can_watch) {
        for (let i in this.auto_skip_rest) {
          let rest_item = this.auto_skip_rest[i];
          if (currentTime >= rest_item.time_start_seconds && currentTime <= rest_item.time_end_seconds) {
            this.$refs.videoPlayer.seek(rest_item.time_end_seconds);
          }
        }
      }
    },

    initRelSplide() {
      if (this.relSplide) {
        try { this.relSplide.destroy(true); } catch(e) { /* noop */ }
        this.relSplide = null;
      }
      var el = this.$refs.relSplide;
      if (!el) return;
      try {
        this.relSplide = new Splide(el, REL_SPLIDE_OPTS).mount();
      } catch(e) {
        /* noop */
      }
    },

    buildFallbackRelatedItems(homeData) {
      var currentId = Number(this.item && this.item.id);
      var maxItems = 18;
      var seen = {};
      var out = [];

      function pushCandidate(candidate) {
        if (!candidate || !candidate.id) return;
        var id = Number(candidate.id);
        if (!id || id === currentId || seen[id]) return;
        seen[id] = true;
        out.push(candidate);
      }

      var promoted = homeData && Array.isArray(homeData.promoted) ? homeData.promoted : [];
      for (var i = 0; i < promoted.length && out.length < maxItems; i++) {
        pushCandidate(promoted[i]);
      }

      var categories = homeData && Array.isArray(homeData.categories) ? homeData.categories : [];
      for (var c = 0; c < categories.length && out.length < maxItems; c++) {
        var catItems = Array.isArray(categories[c].items) ? categories[c].items : [];
        for (var j = 0; j < catItems.length && out.length < maxItems; j++) {
          pushCandidate(catItems[j]);
        }
      }

      return out;
    },

    prepareRelatedRail(done) {
      var finalize = typeof done === 'function' ? done : function() {};
      var sameCategory = Array.isArray(this.item && this.item.rel_items) ? this.item.rel_items : [];
      var filteredSameCategory = sameCategory.filter((rel) => Number(rel.id) !== Number(this.item.id));

      if (filteredSameCategory.length) {
        this.related_mode = 'same_category';
        this.related_items = filteredSameCategory;
        finalize();
        return;
      }

      var applyFallback = (homeData) => {
        this.related_mode = 'fallback';
        this.related_items = this.buildFallbackRelatedItems(homeData || {});
        finalize();
      };

      var cachedHome = this.$store && this.$store.state ? this.$store.state.homepage_data : null;
      if (cachedHome && (Array.isArray(cachedHome.categories) || Array.isArray(cachedHome.promoted))) {
        applyFallback(cachedHome);
        return;
      }

      this.api_call({
        url: 'home',
        data: { include_rel_items: false },
        onComplete: (res) => {
          var homeData = res && res.data && res.data.data ? res.data.data : {};
          if (this.$store && this.$store.state) {
            this.$store.state.homepage_data = homeData;
          }
          applyFallback(homeData);
        },
      });
    },
  },

  watch: {
    is_user_can_watch(newVal) {
      if (newVal === true) {
        this.previewExceeded = false;
        if (this.item && this.item.id) {
          localStorage.removeItem('preview_used_' + this.item.id);
        }
      }
    },
  },

  beforeDestroy() {
    clearInterval(this.updateInterval);
    if (this.relSplide) {
      try { this.relSplide.destroy(true); } catch(e) { /* noop */ }
    }
  },

  mounted() {
    var self = this;
    self.$emit('show_loader');

    this.api_call({
      url: 'items/get',
      data: { id: this.id, eid: this.eid },
      onComplete: function(data) {
        self.seasons = data.data.data.seasons;
        self.cur_season = self.seasons[0];
        if (self.seasons && self.seasons.length) {
          self.activeSeason = self.seasons[0].id;
        }

        self.item = data.data.data;
        self.isFavorite = !!data.data.data.is_favorite;
        if (self.item && self.item.main_category && self.item.main_category.id == 195) {
          self.$router.replace('/info/' + self.item.id);
          return;
        }

        self.episode = data.data.data.episode;

        self.opening_starts = self.episode.opening_starts;
        self.opening_ends   = self.episode.opening_ends;

        if (self.episode.ending_starts > 0) self.ending_starts = self.episode.ending_starts;
        else self.ending_starts = self.episode.duration * 0.98;

        var curr_episode_found = false;
        for (var i in data.data.data.seasons) {
          for (var ep_i in data.data.data.seasons[i].episodes) {
            if (curr_episode_found) {
              self.next_episode = data.data.data.seasons[i].episodes[ep_i];
              break;
            }
            curr_episode_found = (data.data.data.seasons[i].episodes[ep_i].id == data.data.data.episode.id);
          }
          if (self.next_episode && self.next_episode.id > 0) break;
        }

        self.loaded = true;
        self.$emit('loaded');

        if (!self.is_user_can_watch && localStorage.getItem('preview_used_' + self.item.id) === '1') {
          self.previewExceeded = true;
        }

        // Build related rail: same-category first, fallback to recommended/other items.
        self.prepareRelatedRail(() => {
          self.$nextTick(() => self.initRelSplide());
        });
      },
    });
  },
};
</script>

<style lang="scss" scoped src="./videoPage.scss"></style>
